import React, { useState, useEffect, useContext } from 'react';
import {
    Typography,
    Container,
    Box,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    Checkbox,
    Tooltip,
    withStyles,
    TableContainer,
    Chip,
    Grid,
    Card,
    CardContent,
    LinearProgress
} from '@material-ui/core';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { format } from 'date-fns';
import { compose } from 'recompose';
import { withAuthorization } from '../Session';
import FlightOptionsContext from '../Session/flightOptionsContext';
import { isScheduler } from '../../constants';
import { fetchMonthlyPlanData } from '../../services/planService';
import { appStyles } from '../../styles';
import { withFirebase } from '../Firebase';
import SortieSidebar from './SortieSidebar';

const MonthlyPlanPage = (props) => {
    const { classes, firebase } = props;
    const [selectedDate, handleDateChange] = useState(new Date());
    const [flights, setFlights] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedFlight, setSelectedFlight] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const flightOptions = useContext(FlightOptionsContext);

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        fetchMonthlyPlanData(firebase, selectedDate).then(data => {
            if (isMounted) {
                setFlights(data);
                setLoading(false);
            }
        });
        return () => { isMounted = false; };
    }, [firebase, selectedDate]);

    const sortieEvents = flightOptions?.sortieEvents || {};
    const sortieDefaults = flightOptions?.sortieDefaults || {};
    const eventIds = Object.keys(sortieEvents).sort((a, b) => 
        sortieEvents[a].name.localeCompare(sortieEvents[b].name)
    );

    const getCheckboxClass = (status) => {
        if (status === 'COMPLETED') return classes.checkboxGreen;
        if (status === 'UNKNOWN' || !status) return classes.checkboxGrey;
        return classes.checkboxRed;
    };

    const handleFlightClick = (flight) => {
        setSelectedFlight(flight);
        setSidebarOpen(true);
    };

    const handleSidebarSave = (updatedFlight) => {
        // Persist to Firebase
        firebase.flight(updatedFlight.id).update(updatedFlight)
            .then(() => {
                setFlights(flights.map(f => f.id === updatedFlight.id ? updatedFlight : f));
                setSidebarOpen(false);
            })
            .catch(error => {
                console.error("Error updating flight:", error);
                // Handle error (alert?)
            });
    };

    // KPI Calculations
    let plannedTotal = 0;
    let flownTotal = 0;
    let upcomingTotal = 0;
    const now = new Date();

    flights.forEach(f => {
        const takeoff = new Date(f.takeoffTime);
        const land = new Date(f.landTime);
        let scheduled = (land - takeoff) / (1000 * 60 * 60);
        if (scheduled < 0) scheduled += 24;
        
        plannedTotal += scheduled;
        if (f.actualHours) {
            flownTotal += parseFloat(f.actualHours);
        }
        if (takeoff > now) {
            upcomingTotal += scheduled;
        }
    });

    const completionRate = plannedTotal > 0 ? (flownTotal / plannedTotal) * 100 : 0;
    const upcomingRate = plannedTotal > 0 ? (upcomingTotal / plannedTotal) * 100 : 0;

    return (
        <Container maxWidth="xl">
            <div className={classes.offset} />
            <Box mt={2} mb={4}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <DatePicker
                        views={["year", "month"]}
                        label="Year and Month"
                        helperText="Select month to plan"
                        value={selectedDate}
                        onChange={handleDateChange}
                        variant="inline"
                        inputVariant="outlined"
                    />
                </MuiPickersUtilsProvider>
            </Box>

            {loading ? (
                <Typography>Loading flights...</Typography>
            ) : (
                <>
                <TableContainer component={Paper} className={classes.tableContainer}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" className={`${classes.stickyColumn} ${classes.stickyDate} ${classes.stickyHeaderZ}`}>Date</TableCell>
                                <TableCell align="center" className={`${classes.stickyColumn} ${classes.stickyType} ${classes.stickyHeaderZ}`}>Sortie Type</TableCell>
                                <TableCell align="center">Hours Sch</TableCell>
                                <TableCell align="center">Hours Cmpl</TableCell>
                                {eventIds.map(id => (
                                    <TableCell key={id} align="center">
                                        {sortieEvents[id].name}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {flights.map(flight => {
                                const takeoff = new Date(flight.takeoffTime);
                                const land = new Date(flight.landTime);
                                let scheduledHours = (land - takeoff) / (1000 * 60 * 60);
                                if (scheduledHours < 0) scheduledHours += 24;
                                const sortieTypeConfig = sortieDefaults[flight.sortieType] || {};

                                return (
                                <TableRow key={flight.id} hover onClick={() => handleFlightClick(flight)} style={{cursor: 'pointer'}}>
                                        <TableCell align="center" className={`${classes.stickyColumn} ${classes.stickyDate} ${classes.stickyBodyZ}`}>{format(takeoff, 'dd MMM')}</TableCell>
                                        <TableCell align="center" className={`${classes.stickyColumn} ${classes.stickyType} ${classes.stickyBodyZ}`}>
                                            <Chip
                                                label={flight.sortieType}
                                                size="small"
                                                style={{
                                                    backgroundColor: sortieTypeConfig.bgColor || 'default',
                                                    color: sortieTypeConfig.textColor || 'inherit'
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">{scheduledHours.toFixed(1)}</TableCell>
                                        <TableCell align="center">{flight.actualHours || '-'}</TableCell>
                                        {eventIds.map(eventId => {
                                            const event = flight.events ? Object.values(flight.events).find(e => e.type === eventId) : null;
                                            const status = event ? event.status : null;
                                            const checkboxClass = event ? getCheckboxClass(status) : classes.checkboxGrey;
                                            const reason = event?.reason;

                                            return (
                                                <TableCell key={eventId} align="center">
                                                    <Tooltip title={reason || status || (event ? 'Scheduled' : 'Not Scheduled')}>
                                                        <Checkbox
                                                            checked={!!event}
                                                            indeterminate={status !== 'COMPLETED' && status !== 'UNKNOWN' && !!status}
                                                            classes={{ root: checkboxClass, checked: classes.checked }}
                                                            onClick={(e) => e.preventDefault()}
                                                        />
                                                    </Tooltip>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box mt={4}>
                    <Grid container spacing={3} alignItems="stretch">
                        <Grid item xs={12} md={4}>
                            <Card style={{ height: '100%' }}>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>Hours Planned</Typography>
                                    <Typography variant="h4">{plannedTotal.toFixed(1)}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card style={{ height: '100%' }}>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>Hours Flown</Typography>
                                    <Typography variant="h4">{flownTotal.toFixed(1)}</Typography>
                                    <Box mt={2}>
                                        <Typography variant="body2" color="textSecondary">Completion: {completionRate.toFixed(0)}%</Typography>
                                        <LinearProgress variant="determinate" value={Math.min(completionRate, 100)} />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card style={{ height: '100%' }}>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>Hours Upcoming</Typography>
                                    <Typography variant="h4">{upcomingTotal.toFixed(1)}</Typography>
                                    <Box mt={2}>
                                        <Typography variant="body2" color="textSecondary">Remaining: {upcomingRate.toFixed(0)}%</Typography>
                                        <LinearProgress variant="determinate" value={Math.min(upcomingRate, 100)} />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
                </>
            )}
            
            <SortieSidebar
                open={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                flight={selectedFlight}
                sortieDefaults={sortieDefaults}
                sortieEvents={sortieEvents}
                onSave={handleSidebarSave}
            />
        </Container>
    );
};

const condition = isScheduler;

export default compose(
    withFirebase,
    withStyles(appStyles),
    withAuthorization(condition),
)(MonthlyPlanPage);
